import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import { FC } from 'react';

import {
  CarmaImageFit,
  encodeImageSrc,
  getCarmaGenericImageLoader,
  hasImageLoader,
} from 'src/general/helpers/carmaGenericImageLoader';
import { ImageSrcSetSizes } from './Image.type';
import { NextImage } from './NextImage';

type Props = {
  src: string | StaticImport;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  className?: string;
  srcSetSizes?: ImageSrcSetSizes;
  fit?: CarmaImageFit;
  'data-testid'?: string;
};

/**
 * Wrapper around NextJS Image component to allow dynamic selection of the image loader
 */
export const Image: FC<Props> = ({ alt, priority, srcSetSizes, fit, ...props }) => {
  if (typeof props.src === 'string' && (hasImageLoader(props.src) || isSvg(props.src))) {
    const encodedSrc = encodeImageSrc(props.src);
    const responsiveImageProps = getResponsiveImageProps(encodedSrc, props.width, props.height, fit, srcSetSizes);

    return (
      <img
        {...props}
        {...responsiveImageProps}
        alt={alt}
        decoding="async"
        fetchPriority={priority ? 'high' : undefined}
        loading={priority ? 'eager' : 'lazy'}
      />
    );
  }

  // Defaults to NextJS Image component
  return <NextImage {...props} alt={alt} priority={priority} />;
};

const getResponsiveImageProps = (
  src: string,
  width: number,
  height?: number,
  fit?: CarmaImageFit,
  srcSetSizes?: ImageSrcSetSizes,
): { src: string; srcSet?: string; sizes?: string } => {
  // Early return for svg
  if (isSvg(src)) {
    return {
      src,
    };
  }
  // Pixel-density based srcSet
  if (typeof srcSetSizes === 'undefined' || srcSetSizes.length <= 1) {
    const generatedSet = generatePxDensitySrcSet(src, width, height, fit);
    return {
      src: generatedSet[0],
      srcSet: generatedSet[0] !== generatedSet[1] ? generatedSet[1] : undefined,
    };
  }

  // Generate the srcSet based on the sizes
  const loader = getCarmaGenericImageLoader(src);
  const initialSrc = loader({ src, width, height, fit });
  const aspectRatio = height ? height / width : undefined;
  const srcSet = [
    ...new Set(
      [...srcSetSizes]
        .sort((a, b) => (a[1] < b[1] ? -1 : 1))
        .map((value) => {
          return `${loader({ src, width: value[1], height: aspectRatio ? Math.floor(value[1] * aspectRatio) : undefined, fit })} ${value[1]}w`;
        }),
    ),
  ].join(', ');
  const sizes = [
    ...new Set(
      srcSetSizes.map((value) => {
        return `${value[0]} ${value[1]}px`;
      }),
    ),
  ].join(', ');

  return {
    src: initialSrc,
    srcSet,
    sizes,
  };
};

/** Generate the srcSet based on the pixel density */
const generatePxDensitySrcSet = (src: string, width: number, height?: number, fit?: CarmaImageFit) => {
  const loader = getCarmaGenericImageLoader(src);
  const image1X = loader({ src: src, width, height, fit });
  const image2X = loader({ src: src, width: width * 2, height: height ? height * 2 : undefined, fit });

  return [image1X, `${image1X} 1x, ${image2X} 2x`];
};

const isSvg = (src: string) => src.endsWith('.svg');
